import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../../components/JBM/JBMModal/service/modal.service';
import { QueryHttpHelper, SearchColumnType } from '../../../../components/JBM/Helpers/QueryHttpHelper';
import {
  JBMTableColumnAlignment,
  JBMTableColumnDef,
  JBMTableColumnType
} from '../../../../components/JBM/JBMTable/types';
import { TranslateService } from '../../../../services/multilingual/translate.service';
import { TokenService } from '../../../../services/auth/token.service';
import { GroupRights } from '../../../users/data/interfaces/grouprights';
import { PriceAgreement, PriceAgreementState } from '../../data/interfaces/price-agreement';
import { PriceAgreementsService } from '../../data/price-agreements.service';

@Component({
  selector: 'price-agreements',
  templateUrl: './price-agreements.component.html',
  styleUrls: ['./price-agreements.component.scss']
})
export class PriceAgreementsComponent implements OnInit, AfterViewInit, OnDestroy {
  userRights: GroupRights;
  queryHttpHelper: QueryHttpHelper;
  priceAgreement: PriceAgreement;

  items: any[] = [];
  columnDefs: JBMTableColumnDef[];
  loading: boolean = false;
  subject: number = null;
  status: number = null;
  searchboxVisible: boolean = true;
  search: string = '';
  filtersVisible: boolean = false;

  items$: Subscription;
  count$: Subscription;

  @ViewChild('priceAgreementModal') priceAgreementModal: ElementRef;
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;

  constructor(
    private PriceAgreementsService: PriceAgreementsService,
    private ModalService: ModalService,
    private TokenService: TokenService,
    private TranslateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.userRights = this.TokenService.getRightsByName('financial');
    this.priceAgreement = this.PriceAgreementsService.getEmpty();
    this.columnDefs = [
      { name: 'id', type: JBMTableColumnType.id },
      { name: 'subject', header: this.TranslateService.GetTranslation('log.subject'), sortable: true, filterable: true },
      { name: 'description', header: this.TranslateService.GetTranslation('entity.description'), sortable: true, filterable: true },
      { name: 'product', header: this.TranslateService.GetTranslation('entity.product'), sortable: true, filterable: true },
      { name: 'valid_from', type: JBMTableColumnType.date, header: this.TranslateService.GetTranslation('pricelist.valid-from'), sortable: true, filterable: true },
      { name: 'valid_to', type: JBMTableColumnType.date, header: this.TranslateService.GetTranslation('pricelist.valid-to'), sortable: true, filterable: true },
      {
        name: 'state',
        type: JBMTableColumnType.number,
        header: this.TranslateService.GetTranslation('project.state'),
        displayValues: [
          { dataValue: PriceAgreementState.Scheduled, displayValue: 'Scheduled' },
          { dataValue: PriceAgreementState.Active, displayValue: 'Active' },
          { dataValue: PriceAgreementState.Expired, displayValue: 'Expired' },
          { dataValue: PriceAgreementState.Blocked, displayValue: 'Blocked', className: 'text-danger' }
        ],
        width: 8
      },
      { name: 'actions', type: JBMTableColumnType.boolean, align: JBMTableColumnAlignment.right, width: 5 }
    ];

    this.queryHttpHelper = new QueryHttpHelper();
    this.queryHttpHelper.setSortColumn('description');
    this.queryHttpHelper.addSearchColumn('subject', SearchColumnType.string);
    this.queryHttpHelper.addSearchColumn('description', SearchColumnType.string);
    this.queryHttpHelper.addSearchColumn('product', SearchColumnType.string);

    this.getData(true);
  }

  ngAfterViewInit(): void {
    this.columnDefs[7].template = this.actionsTemplate;
  }

  getData(countRows: boolean = false): void {
    this.loading = true;
    const params = this.setFilters(this.queryHttpHelper.getHttpParams(false));
    this.items$ = this.PriceAgreementsService.get(params).subscribe(
      (data: any) => this.items = data.data,
      (error: any) => {
        this.loading = false;
        console.error(error);
      },
      () => {
        this.loading = false;
        if(countRows)
          this.getCount();
      }
    );
  }

  getCount(): void {
    const params = this.setFilters(this.queryHttpHelper.getHttpParams(true));
    this.count$ = this.PriceAgreementsService.get(params).subscribe(
      (data: any) => this.queryHttpHelper.paginationProps.itemCount = data.data,
      (error: any) => console.error(error)
    );
  }

  setFilters(params: HttpParams): HttpParams {
    if(this.subject !== null)
      params = params.append('subject_type', this.subject.toString());
    if(this.status !== null)
      params = params.append('state', this.status.toString());
    return params;
  }

  pageChange(page: any): void {
    this.queryHttpHelper.paginationProps.page = page;
    this.getData();
  }

  sortChange(event: any): void {
    this.queryHttpHelper.setSortColumn(event.name, event.descending);
    this.getData();
  }

  searchChange(event: any): void {
    const value = event.value.length < event.minlength ? '' : event.value;
    if(this.queryHttpHelper.getSearchValue() !== value) {
      this.queryHttpHelper.setSearchValue(value);
      this.getData(true);
    }
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
    this.queryHttpHelper.clearFilters();
    this.queryHttpHelper.setSearchValue('');
    this.getData(true);
  }

  filterChange(event: any): void {
    this.queryHttpHelper.setFilterItem(event.name, event.value);
    this.getData(true);
  }

  rowSelect(row: any): void {
    if(!this.userRights.AllowUpdate)
      return;

    this.loading = true;
    this.PriceAgreementsService.getOne(row.id).subscribe(
      (data: any) => this.priceAgreement = data.data as PriceAgreement,
      (error: any) => {
        this.loading = false;
        console.error(error);
      },
      () => {
        this.loading = false;
        this.ModalService.open(this.priceAgreementModal, 'price-agreement');
      }
    );
  }

  priceAgreementSaved(): void {
    this.ModalService.close('price-agreement');
    this.getData(true);
  }

  priceAgreementCancelled(): void {
    this.ModalService.close('price-agreement');
  }

  ngOnDestroy(): void {
    if(this.items$)
      this.items$.unsubscribe();
    if(this.count$)
      this.count$.unsubscribe();
  }
}
